import React from 'react';

interface ModalErrorBoundaryProps {
  children: React.ReactNode;
}

interface ModalErrorBoundaryState {
  hasError: boolean;
}

/**
 * Isola falhas de renderização/chunk dos modais lazy — evita derrubar o board inteiro.
 */
export class ModalErrorBoundary extends React.Component<
  ModalErrorBoundaryProps,
  ModalErrorBoundaryState
> {
  state: ModalErrorBoundaryState = { hasError: false };

  /**
   * Atualiza estado quando um filho lança erro durante render ou lazy load.
   */
  static getDerivedStateFromError(): ModalErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * Registra erro no console para diagnóstico em produção.
   * @param error - Erro capturado
   * @param info - Stack do React
   */
  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ModalErrorBoundary]', error, info.componentStack);
  }

  /**
   * Reseta o boundary para permitir nova tentativa de abrir o modal.
   */
  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center flex flex-col gap-4">
            <div className="size-12 rounded-full bg-red-50 text-status-red flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined !text-[24px]">error</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Falha ao carregar</h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Não foi possível abrir este painel. Verifique sua conexão e tente novamente.
              </p>
            </div>
            <button
              type="button"
              onClick={this.handleRetry}
              className="w-full py-2.5 bg-primary-accent text-white text-xs font-bold rounded-xl hover:brightness-110 active:scale-[0.98] transition-all"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
